import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import {
  START,
  distanceFromEnd,
  following,
  jump,
  onGrow,
  onScroll,
  toggle,
  type Follow,
} from './followScroll'

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

const toEnd = (smooth: boolean) =>
  window.scrollTo({
    top: document.documentElement.scrollHeight,
    behavior: smooth && !reduced() ? 'smooth' : 'auto',
  })

/**
 * Keeps the page at the end of a growing thread. It watches the thread's
 * height, not a count of what is in it, so streamed words, a card mounting, a
 * "working" line coming and going and a diagram drawing all follow the same
 * way, in one instant scroll per frame. `hold` stops it while a phone keyboard
 * is up (the page is dragged about under the thumbs), and it catches up after.
 */
export const useFollowScroll = (content: RefObject<HTMLElement | null>, hold: boolean) => {
  const state = useRef<Follow>(START)
  const [view, setView] = useState<Follow>(START)
  // scrolled up from the end (a short thread never is): what the jump and the switch wait for
  const [away, setAway] = useState(false)
  const held = useRef(hold)
  const frame = useRef(0)

  const apply = useCallback((next: Follow) => {
    if (next === state.current) return
    state.current = next
    setView(next)
  }, [])

  const pin = useCallback(() => {
    cancelAnimationFrame(frame.current)
    frame.current = requestAnimationFrame(() => {
      if (following(state.current) && !held.current) toEnd(false)
    })
  }, [])

  // a layout effect, so a thread you come back to is at its end on the first frame it paints
  useLayoutEffect(() => {
    let last = window.scrollY
    const onScr = () => {
      const delta = window.scrollY - last
      last = window.scrollY
      const dist = distanceFromEnd(document.documentElement.scrollHeight, window.scrollY, window.innerHeight)
      apply(onScroll(state.current, dist, delta))
      setAway(dist > 160)
    }
    window.addEventListener('scroll', onScr, { passive: true })
    const el = content.current
    let height = el?.getBoundingClientRect().height ?? 0
    const ro = new ResizeObserver(() => {
      const h = el?.getBoundingClientRect().height ?? 0
      const grew = h > height
      height = h
      if (following(state.current)) pin()
      else if (grew) apply(onGrow(state.current))
    })
    if (el) ro.observe(el)
    if (!held.current) toEnd(false)
    pin()
    return () => {
      window.removeEventListener('scroll', onScr)
      ro.disconnect()
      cancelAnimationFrame(frame.current)
    }
  }, [content, apply, pin])

  useEffect(() => {
    held.current = hold
    if (!hold) pin()
  }, [hold, pin])

  return {
    enabled: view.enabled,
    /** new content arrived while the view was not following it */
    unseen: view.unseen,
    /** the view is scrolled up from the end */
    away,
    toggle: () => {
      const next = toggle(state.current)
      apply(next)
      if (following(next)) toEnd(true)
    },
    jump: () => {
      apply(jump(state.current))
      toEnd(true)
    },
    /** for your own words: go to the end now */
    resume: () => {
      apply(jump(state.current))
      pin()
    },
  }
}
